import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { SxProps } from '@mui/system';
import React, { useRef } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { useAppSelector } from '../../app/hooks';
import { flexCenter } from '../commonUI/cssClasses';
import { DiagnosticFeature } from '../commonUI/viewMode/DiagnosticFeature';
import ColorCorrectionErrorIndicator from './ColorCorrectionErrorIndicator';
import ImageController from './ImageController';
import ImageInfo from './ImageInfo';
import {
  selectFrontendColorCorrection,
  selectFrontendColorCorrectionResult,
} from './imageSlice';
import ImageToolbox from './ImageToolbox';
import { ImageProvider } from './providers/ImageProvider';

const commonStyles: SxProps = {
  width: '100%',
  height: '100%',
  ...flexCenter,
};

const ImageAreaContent: React.FC = () => {
  const frontendColorCorrection = useAppSelector(selectFrontendColorCorrection);
  const frontendColorCorrectionResult = useAppSelector(
    selectFrontendColorCorrectionResult
  );
  const imageAreaRef = useRef(null);
  const { width: widthParent = 0, height: heightParent = 0 } =
    useResizeDetector({
      targetRef: imageAreaRef,
    });

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: '100px',
        borderRadius: (theme) => theme.borderRadius.primary,
        display: 'grid',
        gridTemplateRows: 'min-content 1fr min-content',
        gridTemplateColumns: '1fr',
        gridTemplateAreas: `
          'infoArea'
          'imageDisplayArea'
          'toolsArea'
        `,
      }}
    >
      <ImageProvider>
        <Box
          sx={{
            gridArea: 'infoArea',
            width: '100%',
            pointerEvents: 'none',
            position: 'absolute',
            zIndex: 3,
            left: 0,
            top: 0,
          }}
        >
          <ImageInfo />
        </Box>
        <Box
          ref={imageAreaRef}
          sx={{
            gridArea: 'imageDisplayArea',
            ...commonStyles,
            position: 'relative',
          }}
        >
          <ImageController
            widthParent={widthParent}
            heightParent={heightParent}
          />
          <ColorCorrectionErrorIndicator />
          {frontendColorCorrection && (
            <DiagnosticFeature>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '0.5rem',
                  right: '0.5rem',
                  zIndex: 100,
                }}
              >
                <Typography
                  sx={{
                    color: (theme) => theme.colors.common.white,
                    fontSize: '1rem',
                  }}
                >{`Clip: ${frontendColorCorrectionResult.finalClipLimit}`}</Typography>
              </Box>
            </DiagnosticFeature>
          )}
        </Box>
        <Box
          sx={{
            gridArea: 'toolsArea',
            ...commonStyles,
          }}
        >
          <ImageToolbox />
        </Box>
      </ImageProvider>
    </Box>
  );
};

export default ImageAreaContent;
